// WebSocket signalling client for the browser side. It speaks the JSON
// signalling protocol to either a coordinator or a standalone node: it sends
// hello / offer / ice / start / stop and dispatches inbound nodes / answer /
// ice / recvlog / error messages to typed listeners.
//
// The socket implementation is injectable so the client can be exercised with a
// fake in tests; in the worker it defaults to the global WebSocket.

import { type NodeInfo, type RouteHop } from './types';

/** Minimal subset of WebSocket this client relies on. */
export interface SocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

/** Factory that produces a socket for a given URL. */
export type SocketFactory = (url: string) => SocketLike;

/** Inbound signalling messages the client understands. */
export type InboundSignal =
  | { type: 'nodes'; nodes: NodeInfo[] }
  | { type: 'answer'; nodeId: string; runId: number; sdp: string }
  | { type: 'ice'; nodeId: string; runId: number; candidate: RTCIceCandidateInit }
  | {
      type: 'recvlog';
      nodeId: string;
      runId: number;
      fromSeq: number;
      bitmapB64: string;
      tRecvUs: number[];
      final: boolean;
    }
  | { type: 'mtrresult'; nodeId: string; runId: number; hops: RouteHop[]; error?: string }
  | { type: 'error'; nodeId?: string; error: string };

/** Map from message type to its concrete payload shape. */
type InboundByType = {
  [M in InboundSignal as M['type']]: M;
};

/** Listener for a specific inbound message type. */
type Listener<T extends InboundSignal['type']> = (msg: InboundByType[T]) => void;

/** Internal, type-erased listener stored in the registry. */
type AnyListener = (msg: InboundSignal) => void;

/** Options for constructing a SignalingClient. */
export interface SignalingOptions {
  url: string;
  /** Session token appended as ?token= when present. */
  token?: string;
  /** Socket factory; defaults to the global WebSocket. */
  socketFactory?: SocketFactory;
}

function defaultSocketFactory(url: string): SocketLike {
  // The DOM WebSocket satisfies SocketLike structurally.
  return new WebSocket(url) as unknown as SocketLike;
}

/** Typed WebSocket signalling client. */
export class SignalingClient {
  private readonly url: string;
  private readonly factory: SocketFactory;
  private socket: SocketLike | null = null;
  private readonly listeners: Record<InboundSignal['type'], Set<AnyListener>> = {
    nodes: new Set(),
    answer: new Set(),
    ice: new Set(),
    recvlog: new Set(),
    mtrresult: new Set(),
    error: new Set(),
  };
  private closeHandlers = new Set<() => void>();

  constructor(opts: SignalingOptions) {
    const base = opts.url;
    this.url = opts.token
      ? `${base}${base.includes('?') ? '&' : '?'}token=${encodeURIComponent(opts.token)}`
      : base;
    this.factory = opts.socketFactory ?? defaultSocketFactory;
  }

  /** Open the socket and resolve once it is connected. */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const sock = this.factory(this.url);
      this.socket = sock;
      sock.onopen = () => resolve();
      sock.onerror = (ev) => {
        reject(new Error('signaling: socket error'));
        void ev;
      };
      sock.onclose = () => {
        for (const h of this.closeHandlers) h();
      };
      sock.onmessage = (ev) => this.handleMessage(ev.data);
    });
  }

  /** Register a listener; returns an unsubscribe function. */
  on<K extends InboundSignal['type']>(type: K, fn: Listener<K>): () => void {
    const erased = fn as AnyListener;
    this.listeners[type].add(erased);
    return () => {
      this.listeners[type].delete(erased);
    };
  }

  /** Register a socket-closed handler; returns an unsubscribe function. */
  onClose(fn: () => void): () => void {
    this.closeHandlers.add(fn);
    return () => this.closeHandlers.delete(fn);
  }

  private handleMessage(data: unknown): void {
    if (typeof data !== 'string') return;
    let msg: InboundSignal;
    try {
      msg = JSON.parse(data) as InboundSignal;
    } catch {
      return;
    }
    if (!msg || typeof msg.type !== 'string') return;
    switch (msg.type) {
      case 'nodes':
        this.emit('nodes', msg);
        break;
      case 'answer':
        this.emit('answer', msg);
        break;
      case 'ice':
        this.emit('ice', msg);
        break;
      case 'recvlog':
        this.emit('recvlog', msg);
        break;
      case 'mtrresult':
        this.emit('mtrresult', msg);
        break;
      case 'error':
        this.emit('error', msg);
        break;
      default:
        break;
    }
  }

  private emit<K extends InboundSignal['type']>(type: K, msg: InboundByType[K]): void {
    for (const fn of this.listeners[type]) fn(msg as InboundSignal);
  }

  private sendRaw(obj: Record<string, unknown>): void {
    if (!this.socket) throw new Error('signaling: not connected');
    this.socket.send(JSON.stringify(obj));
  }

  /** Ask the coordinator for the node list. */
  hello(wantNodes?: string[], clientVersion = '0.1.0'): void {
    const msg: Record<string, unknown> = { type: 'hello', clientVersion };
    if (wantNodes) msg.wantNodes = wantNodes;
    this.sendRaw(msg);
  }

  /** Send a WebRTC offer for a node/run. */
  offer(nodeId: string, runId: number, sdp: string): void {
    this.sendRaw({ type: 'offer', nodeId, runId, sdp });
  }

  /** Send a local ICE candidate for a node/run. */
  ice(nodeId: string, runId: number, candidate: RTCIceCandidateInit): void {
    this.sendRaw({ type: 'ice', nodeId, runId, candidate });
  }

  /** Tell the node to start a run with the given parameters. */
  start(
    nodeId: string,
    runId: number,
    expected: number,
    sizes: number[],
    pps: number,
    durationMs: number,
  ): void {
    this.sendRaw({ type: 'start', nodeId, runId, expected, sizes, pps, durationMs });
  }

  /** Tell the node to stop a run. */
  stop(nodeId: string, runId: number): void {
    this.sendRaw({ type: 'stop', nodeId, runId });
  }

  /** Ask the node to run a reverse route trace toward this client. */
  mtr(nodeId: string, runId: number): void {
    this.sendRaw({ type: 'mtr', nodeId, runId });
  }

  /** Close the underlying socket. */
  close(): void {
    this.socket?.close();
    this.socket = null;
  }
}

/**
 * The subset of the signalling API a NodeConnection needs to complete a
 * WebRTC handshake. SignalingClient satisfies it directly.
 */
export interface ConnectionSignaling {
  offer(nodeId: string, runId: number, sdp: string): void;
  ice(nodeId: string, runId: number, candidate: RTCIceCandidateInit): void;
}
